import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Bundle Group — orchestrates multiple Bundle Items, syncs variant dropdowns
 * with the main product variant picker, and recomputes pricing live.
 *
 * This component owns the bundle SELECTION UI only. It does NOT write to the
 * cart. The single owner of the cart-write path is assets/bundle-cart.js, which
 * reads the active tier's variant selections at add-to-cart time. See that file
 * for the property contract (product-type / _bundle_id / Gift Included).
 *
 * The Liquid render establishes structure; this component owns runtime state.
 *
 * @typedef {{ id: number, title: string, price: number, compare_at_price: number,
 *             available: boolean, option1: string|null, option2: string|null,
 *             option3: string|null, featured_image: string }} VariantRow
 */
class BundleGroupComponent extends Component {
  /** @type {VariantRow[]} */
  #variants = [];

  /** @type {Map<number, VariantRow>} */
  #variantById = new Map();

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {string} */
  #moneyFormat = '{{amount}}';

  /** @type {string} */
  #currency = 'USD';

  /** @type {boolean} */
  #hasVariants = false;

  /**
   * When true, a pending main-picker variant:update was triggered by US (a bundle
   * slot change, to update the featured media) — so #onMainVariantUpdate must NOT
   * mirror that value back into the other slots. Keeps slots independent while
   * still driving Horizon's native variant→media flow.
   * @type {boolean}
   */
  #suppressSlotMirror = false;

  /** @type {AbortController} */
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // --- Read JSON variant catalogue ---
    const variantsEl = this.querySelector('script[data-bundle-variants]');
    try {
      this.#variants = variantsEl ? JSON.parse(variantsEl.textContent || '[]') : [];
    } catch {
      this.#variants = [];
    }
    this.#variantById = new Map(this.#variants.map((v) => [Number(v.id), v]));

    this.#moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.#currency = ((window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD').toUpperCase();
    this.#hasVariants = this.dataset.hasVariants === 'true';

    // --- Locate the product form so we can read the selected variant id and
    // update the add-to-cart label. Cart writes are owned entirely by
    // assets/bundle-cart.js, which reads our active tier at submit time — we no
    // longer hijack the form's submit here. ---
    const formId = this.dataset.productFormId;
    if (formId) {
      const form = document.getElementById(formId);
      if (form instanceof HTMLFormElement) {
        this.#form = form;
      }
    }

    const { signal } = this.#abort;

    // --- Wire delegated change handler (radios + selects) ---
    this.addEventListener('change', this.#onChange, { signal });

    // --- Watch the main variant picker so dropdowns can mirror it ---
    const section = this.closest('.shopify-section') || this.closest('section') || document;
    section.addEventListener(ThemeEvents.variantUpdate, this.#onMainVariantUpdate, { signal });

    // --- Make sure exactly one tier starts as selected ---
    this.#ensureSelectedTier();
    this.#syncSelectedClass();
    this.#renderAllPrices();
    this.#updateAddToCartText();
    this.#broadcastSubtotal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
  }

  // -----------------------------------------------------------------
  // Event handlers
  // -----------------------------------------------------------------

  /** @param {Event} event */
  #onChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target) return;

    if (target.matches('[data-bundle-radio]')) {
      this.#syncSelectedClass();
      this.#renderAllPrices();
      this.#updateAddToCartText();
      this.#broadcastSubtotal();
      return;
    }

    if (target.matches('[data-bundle-variant-select]')) {
      // Remember the user has overridden this slot so future main-variant
      // changes don't trample their choice.
      target.dataset.userSet = 'true';
      // Drive Horizon's native variant picker so the featured media gallery
      // updates to this slot's variant (media-gallery listens to variant:update).
      // Guard the resulting variant:update so #onMainVariantUpdate does NOT mirror
      // this value into sibling slots — that keeps each slot independent. If the
      // picker didn't actually move (variant already current, no variant:update
      // will fire), clear the guard immediately so it can't get stuck.
      this.#suppressSlotMirror = true;
      const moved = this.#syncMainPickerFromVariant(Number(target.value));
      if (!moved) this.#suppressSlotMirror = false;
      const tier = target.closest('[data-bundle-item]');
      if (tier) this.#renderTierPrice(tier);
      if (tier && tier.contains(this.#getCheckedRadio()?.parentElement || null)) {
        this.#updateAddToCartText();
        this.#broadcastSubtotal();
      } else if (this.#isTierActive(tier)) {
        this.#updateAddToCartText();
        this.#broadcastSubtotal();
      }
    }
  };

  /**
   * Mirror the main product variant picker into the bundle dropdowns.
   * Only updates slots the user hasn't manually overridden.
   * @param {CustomEvent} event
   */
  #onMainVariantUpdate = (event) => {
    const variantId = Number(event?.detail?.resource?.id);
    if (!variantId || !this.#variantById.has(variantId)) return;

    // Skip mirroring when this variant:update was triggered by a bundle SLOT
    // change (we only wanted the featured media to follow). Genuine changes to
    // the MAIN picker still mirror into untouched slots.
    if (this.#suppressSlotMirror) {
      this.#suppressSlotMirror = false;
    } else {
      const selects = /** @type {NodeListOf<HTMLSelectElement|HTMLInputElement>} */ (
        this.querySelectorAll('[data-bundle-variant-select]')
      );

      for (const sel of selects) {
        if (sel.dataset.userSet === 'true') continue;
        if (sel instanceof HTMLSelectElement) {
          sel.value = String(variantId);
        } else if (sel instanceof HTMLInputElement) {
          sel.value = String(variantId);
        }
      }
    }

    this.#renderAllPrices();
    this.#updateAddToCartText();
    this.#broadcastSubtotal();
  };

  /**
   * Drive the MAIN product variant picker to the given variant so Horizon's
   * native variant:update flow runs and swaps the featured image (and price,
   * sku, etc). We resolve the variant's option values, set the matching option
   * control in each fieldset/select, then dispatch the same 'change' the picker
   * already listens for. We add no fetch logic and touch nothing in the bundle
   * DOM — the picker morphs only itself and the media gallery replaces only
   * itself, so bundle selections survive untouched.
   * @param {number} variantId
   * @returns {boolean} true if the picker moved and dispatched a change (a
   *   variant:update will follow); false if nothing changed.
   */
  #syncMainPickerFromVariant(variantId) {
    const variant = this.#variantById.get(Number(variantId));
    if (!variant) return false;

    const section = this.closest('.shopify-section') || this.closest('section') || document;
    const picker =
      section.querySelector('variant-picker[data-template-product-match="true"]') ||
      section.querySelector('variant-picker');
    // No picker rendered (product has only a default variant) — nothing to sync.
    if (!picker) return false;

    const optionValues = [variant.option1, variant.option2, variant.option3];

    /** @type {HTMLElement | null} */
    let lastChanged = null;

    for (let i = 0; i < optionValues.length; i++) {
      const value = optionValues[i];
      if (value == null || value === '') continue;

      // Buttons / swatches render as a <fieldset> keyed by the option index.
      const fieldset = picker.querySelector(`fieldset[data-fieldset-index="${i}"]`);
      if (fieldset) {
        const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
          fieldset.querySelectorAll('input[type="radio"]')
        );
        for (const radio of radios) {
          if (radio.value === value) {
            if (!radio.checked) {
              radio.checked = true;
              lastChanged = radio;
            }
            break;
          }
        }
        continue;
      }

      // Dropdowns render as a <select> whose id ends with the option index.
      // The picker reads selections via `option[selected]`, so set the attribute
      // (not just .value) for every select we move, otherwise its request URL
      // would carry a stale option value.
      const selects = /** @type {NodeListOf<HTMLSelectElement>} */ (picker.querySelectorAll('select'));
      for (const select of selects) {
        if ((select.id.split('-').pop() || '') !== String(i)) continue;
        const option = Array.from(select.options).find((o) => o.value === value);
        if (option && !option.selected) {
          for (const o of select.options) o.removeAttribute('selected');
          option.setAttribute('selected', 'selected');
          select.value = value;
          lastChanged = select;
        }
        break;
      }
    }

    // Nothing moved — the main picker already shows this variant. Skip the
    // dispatch so we don't fire a redundant section fetch.
    if (!lastChanged) return false;

    lastChanged.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  // -----------------------------------------------------------------
  // Selection helpers
  // -----------------------------------------------------------------

  /**
   * Public accessor used by assets/bundle-cart.js to read the active tier's
   * chosen variant ids at add-to-cart time. Returns null when no tier is
   * active so the caller can fall back to the plain product variant.
   * @returns {number[] | null}
   */
  getActiveSelections() {
    const tier = this.#getActiveTier();
    if (!tier) return null;
    const ids = this.#getTierVariantIds(tier);
    return ids.length ? ids : null;
  }

  #ensureSelectedTier() {
    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('[data-bundle-radio]')
    );
    if (radios.length === 0) return;
    const anyChecked = Array.from(radios).some((r) => r.checked);
    if (!anyChecked) radios[0].checked = true;
  }

  /** @returns {HTMLInputElement | null} */
  #getCheckedRadio() {
    return /** @type {HTMLInputElement | null} */ (
      this.querySelector('[data-bundle-radio]:checked')
    );
  }

  /** @returns {HTMLElement | null} */
  #getActiveTier() {
    const radio = this.#getCheckedRadio();
    return radio ? /** @type {HTMLElement} */ (radio.closest('[data-bundle-item]')) : null;
  }

  /** @param {HTMLElement | null} tier */
  #isTierActive(tier) {
    if (!tier) return false;
    const radio = /** @type {HTMLInputElement | null} */ (tier.querySelector('[data-bundle-radio]'));
    return !!radio?.checked;
  }

  #syncSelectedClass() {
    const tiers = this.querySelectorAll('[data-bundle-item]');
    for (const tier of tiers) {
      const radio = /** @type {HTMLInputElement | null} */ (tier.querySelector('[data-bundle-radio]'));
      tier.classList.toggle('is-selected', !!radio?.checked);
    }
  }

  // -----------------------------------------------------------------
  // Variant id extraction
  // -----------------------------------------------------------------

  /**
   * For a given tier, returns the variant ids the user has chosen for each slot.
   * Falls back to the product's currently selected variant for unset slots
   * (e.g. when a tier's variant selectors are hidden because show_variant_selectors
   * is off — typical for the "Buy 1" single-item tier).
   * @param {HTMLElement} tier
   * @returns {number[]}
   */
  #getTierVariantIds(tier) {
    const quantity = Number(tier.dataset.bundleQuantity) || 1;
    const selects = /** @type {NodeListOf<HTMLSelectElement|HTMLInputElement>} */ (
      tier.querySelectorAll('[data-bundle-variant-select]')
    );

    /** @type {number[]} */
    const ids = [];
    for (const sel of selects) {
      const value = Number(sel.value);
      if (value && this.#variantById.has(value)) ids.push(value);
    }

    if (ids.length === quantity) return ids;

    // Fallback: read the form's main variant id and pad to quantity.
    const fallback = this.#getMainVariantId();
    if (!fallback) return ids;
    while (ids.length < quantity) ids.push(fallback);
    return ids;
  }

  /** @returns {number | null} */
  #getMainVariantId() {
    if (!this.#form) {
      const stored = /** @type {HTMLInputElement | null} */ (
        this.querySelector('[data-bundle-active-variant]')
      );
      return stored ? Number(stored.value) || null : null;
    }
    const input = /** @type {HTMLInputElement | null} */ (this.#form.querySelector('input[name="id"]'));
    const v = input ? Number(input.value) : NaN;
    return Number.isFinite(v) && v > 0 ? v : null;
  }

  // -----------------------------------------------------------------
  // Pricing
  // -----------------------------------------------------------------

  /** @param {HTMLElement} tier */
  #computeTierPricing(tier) {
    const discount = Number(tier.dataset.bundleDiscount) || 0;
    const factor = (100 - discount) / 100;
    // Optional flat currency discount (in dollars). When > 0 it overrides the
    // percentage: a fixed amount comes off the bundle total, mirroring how the
    // percentage applies to the whole-bundle total. Falls back to the existing
    // percentage path when unset/zero, so tiers without it are unchanged.
    const fixedAmount = Number(tier.dataset.bundleFixedAmount) || 0;

    const variantIds = this.#getTierVariantIds(tier);

    let totalPrice = 0;
    let totalCompare = 0;

    for (const id of variantIds) {
      const v = this.#variantById.get(id);
      if (!v) continue;
      totalPrice += Number(v.price) || 0;
      const compare = Number(v.compare_at_price) || 0;
      totalCompare += compare > Number(v.price) ? compare : Number(v.price) || 0;
    }

    const currentCents =
      fixedAmount > 0
        ? Math.max(0, totalPrice - Math.round(fixedAmount * 100))
        : Math.round(totalPrice * factor);
    // Guarantee compare >= current so the strike-through never looks broken.
    const compareCents = Math.max(totalCompare, totalPrice);
    const savingsCents = Math.max(0, compareCents - currentCents);

    return { currentCents, compareCents, savingsCents };
  }

  /** @param {Element} tier */
  #renderTierPrice(tier) {
    const { currentCents, compareCents, savingsCents } = this.#computeTierPricing(/** @type {HTMLElement} */ (tier));

    const currentEl = tier.querySelector('[data-bundle-price-current]');
    const compareEl = tier.querySelector('[data-bundle-price-compare]');
    const saveEl = tier.querySelector('[data-bundle-price-save]');

    if (currentEl) currentEl.textContent = this.#formatMoney(currentCents);

    if (compareEl) {
      if (compareCents > currentCents) {
        compareEl.textContent = this.#formatMoney(compareCents);
        compareEl.removeAttribute('hidden');
      } else {
        compareEl.textContent = '';
        compareEl.setAttribute('hidden', '');
      }
    }

    if (saveEl) {
      if (savingsCents > 0) {
        saveEl.textContent = `Save ${this.#formatMoney(savingsCents)}`;
        saveEl.removeAttribute('hidden');
      } else {
        saveEl.setAttribute('hidden', '');
      }
    }
  }

  #renderAllPrices() {
    const tiers = this.querySelectorAll('[data-bundle-item]');
    for (const tier of tiers) this.#renderTierPrice(tier);
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    try {
      return formatMoney(cents, this.#moneyFormat, this.#currency);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  // -----------------------------------------------------------------
  // Add-to-cart button — append the bundle's current price.
  // -----------------------------------------------------------------

  #updateAddToCartText() {
    if (!this.#form) return;

    const tier = this.#getActiveTier();
    if (!tier) return;

    const { currentCents } = this.#computeTierPricing(tier);
    const priceText = this.#formatMoney(currentCents);

    const btn = this.#form.querySelector('add-to-cart-component button');
    if (!btn) return;

    // The default markup is: <span class="svg-wrapper">…</span><span>Add to cart</span>
    const textNode = btn.querySelector('span:not(.svg-wrapper)');
    if (!textNode) return;

    if (!textNode.dataset.bundleBaseText) {
      textNode.dataset.bundleBaseText = (textNode.textContent || '').trim() || 'Add to cart';
    }
    const base = textNode.dataset.bundleBaseText;
    textNode.textContent = `${base} — ${priceText}`;
  }

  // -----------------------------------------------------------------
  // Subtotal broadcast — drives offer unlock components in the section.
  // -----------------------------------------------------------------

  /**
   * Public hook so siblings (e.g. offer-unlock-group) that mount after the
   * bundle's first broadcast can request a fresh snapshot.
   */
  publishSubtotal() {
    this.#broadcastSubtotal();
  }

  #broadcastSubtotal() {
    const tier = this.#getActiveTier();
    if (!tier) return;

    const { currentCents } = this.#computeTierPricing(tier);
    const detail = {
      sectionId: this.dataset.sectionId || null,
      productId: this.dataset.productId || null,
      bundleId: tier.dataset.bundleId || null,
      subtotalCents: currentCents,
      subtotal: currentCents / 100,
      formattedSubtotal: this.#formatMoney(currentCents),
      currency: this.#currency,
    };

    // Section-scoped event for offer components in the same product section.
    const section = this.closest('.shopify-section') || this.closest('section') || document;
    section.dispatchEvent(new CustomEvent('bundle:subtotal:update', { detail, bubbles: true }));
    // Document-scoped so future listeners (sticky bars, drawers) can hook in too.
    document.dispatchEvent(new CustomEvent('bundle:subtotal:update', { detail }));

    // Offer-group contract — same payload, normalized field names.
    document.dispatchEvent(
      new CustomEvent('titan:price:update', {
        detail: {
          sectionId: this.dataset.sectionId || null,
          totalPrice: currentCents,
        },
      })
    );
  }
}

if (!customElements.get('bundle-group-component')) {
  customElements.define('bundle-group-component', BundleGroupComponent);
}
